import React, { forwardRef, useState } from 'react';
import SearchIcon from '@assets/icons/search-magnifying-glass.svg';
import { createStyles } from '@/contexts/theme-context';
import {
  NativeSyntheticEvent,
  Platform,
  TextInput,
  TextInputKeyPressEventData,
  TextInputProps,
  TextInputSubmitEditingEventData,
  View,
} from 'react-native';

interface SearchInputProps {
  autoFocus?: boolean;
  maxLength?: number;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  onSearch: (text: string) => void;
  onSubmitEditing?: (
    e: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
  ) => void;
  placeholderText?: string;
  rightElement?: React.ReactNode;
  searchText: string;
  selectOnFocus?: boolean;
  submitBehavior?: TextInputProps['submitBehavior'];
}

export const SearchInput = forwardRef<TextInput | null, SearchInputProps>(
  (
    {
      autoFocus,
      maxLength,
      onBlur,
      onFocus,
      onKeyPress,
      onSearch,
      onSubmitEditing,
      placeholderText,
      rightElement,
      searchText,
      selectOnFocus,
      submitBehavior,
    },
    ref,
  ) => {
    const [styles, theme] = useStyles();
    const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
    const [selection, setSelection] = useState<
      { end: number; start: number } | undefined
    >();

    const handleFocus = () => {
      setIsSearchInputFocused(true);
      onFocus?.();
      // Select all text on focus if selectOnFocus is enabled
      if (selectOnFocus && searchText.length > 0) {
        const length = searchText.length;
        setSelection({ end: length, start: 0 });
      }
    };

    const handleBlur = () => {
      setIsSearchInputFocused(false);
      onBlur?.();
    };

    const handleSelectionChange = () => {
      // Clear selection state after it's been applied
      if (selection) {
        setSelection(undefined);
      }
    };

    return (
      <View
        style={[
          styles.searchInputContainer,
          isSearchInputFocused ? styles.searchInputContainerFocused : undefined,
        ]}
      >
        <SearchIcon
          height={16}
          stroke={theme.sk.color.icon.tertiary}
          width={16}
        />
        <TextInput
          autoFocus={autoFocus}
          maxLength={maxLength}
          onBlur={handleBlur}
          onChangeText={onSearch}
          onFocus={handleFocus}
          onKeyPress={onKeyPress}
          onSelectionChange={handleSelectionChange}
          onSubmitEditing={onSubmitEditing}
          placeholder={placeholderText}
          placeholderTextColor={theme.sk.color.text.hint}
          ref={ref}
          selection={selection}
          selectionColor={theme.sk.color.border.input.active}
          style={[
            styles.searchInput,
            !searchText ? styles.searchInputPlaceholder : null,
          ]}
          submitBehavior={submitBehavior}
          value={searchText}
        />
        {rightElement}
      </View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  searchInput: {
    ...theme.sk.typography.input.md,
    color: theme.sk.color.text.primary,
    flex: 1,
    fontWeight: 400,
    minWidth: 0,
    ...(Platform.OS === 'web' ? { outlineWidth: 0 } : {}),
  },
  searchInputContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderColor: 'transparent', // prevent jutter when focus styles are toggled
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: 8,
    height: 40,
    paddingHorizontal: 12,
  },
  searchInputContainerFocused: {
    borderColor: theme.sk.color.bg.button.primary.default,
  },
  searchInputPlaceholder: {
    color: theme.sk.color.text.hint,
  },
}));
